import React, {
  useEffect,
  useState,
  useRef,
  createRef,
} from 'react';
import {
  values, isEmpty,
} from 'lodash';
import PropTypes from 'prop-types';
import {
  EVBox,
  EVTypography,
  EVGrid,
  EVInputLabel,
  EVCheckbox,
  EVButton,
  EVLink,
  EVFormHelperText,
  EVBackdrop,
  EVCircularProgress,
  EVRefreshOutlinedIcon,
} from '@eagleview/ev-comp-library';
import { useHistory } from 'react-router-dom';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import {
  COUNTRY_LIST, PRIVACY_POLICY_URL, RegistrationFieldNames, TERMS_OF_USE_URL,
} from '../../constants/AppConstants';
import {
  INIT_INPUT_VALUES,
  INIT_INPUT_ERRORS,
  INIT_ADDRESS,
  INIT_ADDRESS_ERRORS,
  INIT_ALERT_ERRORS,
} from '../../constants/InitStateValues';
import useStyles from './SignUpStyle';
import FormSelectField from '../../commonComponents/form-select-field';
import InputField from '../../commonComponents/input-field';
import Address from '../../commonComponents/address';
import validateOrgInput from '../../utils/validateOrgInput.util';
import validateAddressInput from '../../utils/validateAddressInput.util';
import createAccount from '../../services/createAccount.service';
import getIndustries from '../../services/getIndustries.service';
import getBusinessSegments from '../../services/getBusinessSegments.service';
import getStateList from '../../services/getStateList.service';
import getCustomerReferral from '../../services/getCustomerReferral.service';
import ErrorsAlert from './ErrorsAlert.component';
import useMediaQuery from '../../hooks/useMediaQuery';
import evLogo from '../../assets/images/evLogo.svg';

const SignUp = (props) => {
  const { ldClient, flags: { enableGovtIndustry } } = props;
  const classes = useStyles();

  const [serviceError, setServiceError] = useState({});
  const [refererList, setRefererList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [industryList, setIndustryList] = useState([]);
  const [businessSegmentList, setBusinessSegmentList] = useState([]);
  const [inputValues, setInputValues] = useState(INIT_INPUT_VALUES);
  const [errorMessages, setErrorMessages] = useState(INIT_INPUT_ERRORS);
  const [contactAddress, setContactAddress] = useState(INIT_ADDRESS);
  const [contactAddressErrors, setContactAddressErrors] = useState(INIT_ADDRESS_ERRORS);
  const [isBillingInfoSame, setIsBillingInfoSame] = useState(true);
  const [isEmailNotificationsAccepted, setIsEmailNotificationsAccepted] = useState(true);
  const [billingInfo, setBillingInfo] = useState(INIT_INPUT_VALUES);
  const [billingInfoErrors, setBillingInfoErrors] = useState(INIT_INPUT_ERRORS);
  const [billingAddress, setBillingAddress] = useState(INIT_ADDRESS);
  const [billingAddressErrors, setBillingAddressErrors] = useState(INIT_ADDRESS_ERRORS);
  const [isTermsAccepted, setIsTermsAccepted] = useState(false);
  const [stateList, setStateList] = useState([]);
  const [canadaStateList, setCanadaStateList] = useState([]);
  const [UsStateList, setUsStateList] = useState([]);
  const [flagReady, setFlagReady] = useState(false);
  const mountedRef = useRef(false);
  const imageDivRef = useRef(null);
  const formDivRef = useRef(null);
  const history = useHistory();
  const [alertErrorMessages, setAlertErrorMessages] = useState(INIT_ALERT_ERRORS);
  const [isStateTriggeredCountryChange, setIsStateTriggeredCountryChange] = useState(false);

  const [errorsKey, setErrorKey] = useState(true);
  const inputRefs = Object.keys(RegistrationFieldNames).reduce((refs, key) => ({ ...refs, [key]: createRef() }), {});

  /**
   * async function to call API and handle response
   * @param {Function} service - service function called for API
   * @param {Function} successCallback - function called with response data if res.ok is true
  */
  const isMobile = useMediaQuery(640);
  const handleAPICall = async (service, successCallback, identifier) => {
    try {
      const res = await service();
      const { ok, json } = res;
      if (ok) successCallback(json);
      else setServiceError({ [identifier]: json.errorDescription });
    } catch (err) {
      setServiceError({ identifier: err.message });
    }
  };

  const handleBusinessSegmentUpdate = () => {
    const { industry } = inputValues;

    setInputValues((prev) => ({ ...prev, businessSegment: 0 }));
    setBusinessSegmentList([]);
    setServiceError({});

    if (industry) {
      handleAPICall(() => getBusinessSegments(industry), setBusinessSegmentList, 'businessSegment');
    }
  };

  useEffect(() => {
    ldClient.identify({ key: 'key' }, null, () => {
      setFlagReady(true);
    });
  }, [ldClient]);

  useEffect(() => {
    if (isBillingInfoSame) setBillingAddress(INIT_ADDRESS);
  }, [isBillingInfoSame]);

  useEffect(() => {
    const { country } = contactAddress;
    if (country) {
      if (country === 229) {
        const states = UsStateList;
        const [{ id: state }] = states;
        setStateList(states);
        setContactAddress((prevValues) => ({ ...prevValues, state: isStateTriggeredCountryChange ? contactAddress.state : state }));
        setBillingAddress((prevAddress) => ({ ...prevAddress, country, state: isStateTriggeredCountryChange ? contactAddress.state : state }));
      } else if (country === 39) {
        const states = canadaStateList;
        const [{ id: state }] = states;
        setStateList(states);
        setContactAddress((prevValues) => ({ ...prevValues, state: isStateTriggeredCountryChange ? contactAddress.state : state }));
        setBillingAddress((prevAddress) => ({ ...prevAddress, country, state: isStateTriggeredCountryChange ? contactAddress.state : state }));
      }
    } else {
      setBillingAddress((prevAddress) => ({ ...prevAddress, country: 0 }));
    }
    setIsStateTriggeredCountryChange(false);
  }, [contactAddress.country]);

  const stateExists = (stateArray, searchId) => stateArray.map((item) => item.id).includes(searchId);

  useEffect(() => {
    const { state } = contactAddress;
    const isUsState = stateExists(UsStateList, state);
    if (isUsState) {
      const country = 229;
      if (contactAddress.country !== 229) {
        setIsStateTriggeredCountryChange(true);
      }
      setContactAddress((prevValues) => ({ ...prevValues, country }));
      setBillingAddress((prevAddress) => ({ ...prevAddress, country }));
    } else if (stateExists(canadaStateList, state)) {
      const country = 39;
      if (contactAddress.country !== 39) {
        setIsStateTriggeredCountryChange(true);
      }
      setContactAddress((prevValues) => ({ ...prevValues, country }));
      setBillingAddress((prevAddress) => ({ ...prevAddress, country }));
    }
  }, [contactAddress.state]);

  useEffect(() => {
    setInputValues((input) => ({ ...input, shippingEmail: '' }));
  }, [isEmailNotificationsAccepted, setInputValues]);

  useEffect(() => {
    if (mountedRef.current) {
      setErrorMessages((prevMsgs) => ({ ...prevMsgs, terms: isTermsAccepted ? '' : 'Please agree to the "Terms of Use" and "Privacy Policy".' }));
    }
  }, [isTermsAccepted]);

  useEffect(() => {
    handleBusinessSegmentUpdate();
  }, [inputValues.industry]);

  useEffect(() => {
    setInputValues((input) => ({ ...input, shippingEmail: '' }));
  }, [isEmailNotificationsAccepted, setInputValues]);

  useEffect(() => {
    handleAPICall(getCustomerReferral, setRefererList, 'referer');
    mountedRef.current = true;
    handleAPICall(
      () => getStateList(229),
      (states) => {
        const [{ id: state }] = states;
        setUsStateList(states);
        setStateList((prevValues) => ([...prevValues, ...states]));
        setContactAddress((prevValues) => ({ ...prevValues, state }));
        setBillingAddress((prevAddress) => ({ ...prevAddress, state }));
      },
      'state',
    );
    handleAPICall(
      () => getStateList(39),
      (states) => {
        setCanadaStateList(states);
        setStateList((prevValues) => ([...prevValues, ...states]));
      },
      'state',
    );
  }, []);

  useEffect(() => {
    if (flagReady) handleAPICall(() => getIndustries(enableGovtIndustry), setIndustryList, 'industry');
  }, [flagReady]);

  const handleWheel = (event) => {
    const formDiv = formDivRef.current;
    if (formDiv) {
      formDiv.scrollBy({
        top: event.deltaY,
        behavior: 'auto',
      });
    }
  };

  const retryApiCalls = () => {
    setServiceError({});
    handleAPICall(getCustomerReferral, setRefererList, 'referer');
    if (flagReady) handleAPICall(() => getIndustries(enableGovtIndustry), setIndustryList, 'industry');
  };

  const handleInputChange = (event) => {
    const { target: { id, value } } = event;
    const inputs = { ...inputValues };
    inputs[id] = value;
    setInputValues(inputs);
  };

  const handleInputBlur = (event) => {
    const { target: { id } } = event;
    const errors = { ...errorMessages };
    const key = ((id === 'shippingEmail') && !isEmailNotificationsAccepted) ? 'email' : id;
    const error = validateOrgInput({ [key]: inputValues[id] });
    errors[id] = error[key];
    setErrorMessages(errors);
  };

  const handleBillingInputBlur = (event) => {
    const { target: { id } } = event;
    const billingId = id.replace('billing-', '');
    const errors = { ...billingInfoErrors };
    errors[billingId] = validateOrgInput({ [billingId]: billingInfo[billingId] })[billingId];
    setBillingInfoErrors(errors);
  };

  const handleBillingInfoChange = (event) => {
    const { target: { id, value } } = event;
    const billlingId = id.replace('billing-', '');
    const inputs = { ...billingInfo };
    inputs[billlingId] = value;
    setBillingInfo(inputs);
  };

  const scrollToTop = () => {
    if (imageDivRef.current) {
      imageDivRef.current.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  };

  const handleEmailCheckboxChange = (event) => setIsEmailNotificationsAccepted(event.target.checked);
  const handleBillingCheckboxChange = (event) => setIsBillingInfoSame(event.target.checked);
  const handleTermsCheckboxChange = (event) => setIsTermsAccepted(event.target.checked);

  const handleSubmit = async () => {
    setErrorKey((key) => (!key));
    const clientIdsearchParam = (new URLSearchParams(window.location.search)).get('clientId');
    const clientId = clientIdsearchParam === 'null' ? '' : clientIdsearchParam;
    const is3Legged = (new URLSearchParams(window.location.search)).get('is3legged') || false;
    setLoading(true);
    const inputErrors = validateOrgInput(inputValues);
    let { shippingEmail } = inputValues;
    if (!isEmailNotificationsAccepted) inputErrors.shippingEmail = validateOrgInput({ email: shippingEmail }).email;
    else shippingEmail = inputValues.email;
    if (!isTermsAccepted) inputErrors.terms = 'Please agree to the "Terms of Use" and "Privacy Policy".';
    const contactAddressErr = validateAddressInput(contactAddress);
    setErrorMessages(inputErrors);

    let billingInfoErr;
    let billingAddressErr;
    const contactInformation = {
      ...inputValues,
      address: {
        ...contactAddress,
        ...(contactAddress.country && {
          country: COUNTRY_LIST.find(({ countryId }) => countryId === contactAddress.country).name,
          state: stateList.find(({ id }) => id === contactAddress.state).name,
          phone: {
            ...contactAddress.phone,
            countryId: contactAddress.country,
          },
        }),
      },
    };
    let billingInformation = contactInformation;
    setContactAddressErrors(contactAddressErr);
    const contactError = {
      country: contactAddressErr.country,
      contactPhone: contactAddressErr.phone,
      contactCity: contactAddressErr.city,
      contactState: contactAddressErr.state,
      contactAddress1: contactAddressErr.streetAddress1,
      contactZipCode: contactAddressErr.zipCode,
    };
    setAlertErrorMessages((prev) => ({
      ...prev,
      ...contactError,
      ...inputErrors,
      hasValidationErrors: (contactAddressErr.hasValidationErrors || inputErrors.hasValidationErrors),
    }));
    if (!isBillingInfoSame) {
      const billingInputs = { ...billingInfo };
      billingInputs.refer = inputValues.refer;
      setBillingInfo(billingInputs);
      billingInfoErr = validateOrgInput(billingInputs);
      delete billingInfoErr.industry;
      delete billingInfoErr.businessSegment;
      billingAddressErr = validateAddressInput(billingAddress);
      setBillingInfoErrors(validateOrgInput(billingInputs));
      setBillingAddressErrors(validateAddressInput(billingAddress));
      const billingError = {
        billingEmail: billingInfoErr.email,
        billingFirstName: billingInfoErr.firstName,
        billingLastName: billingInfoErr.lastName,
        billingCompany: billingInfoErr.company,
        billingPhone: billingAddressErr.phone,
        billingCity: billingAddressErr.city,
        billingState: billingAddressErr.state,
        billingAddress1: billingAddressErr.streetAddress1,
        billingZipCode: billingAddressErr.zipCode,
        hasValidationErrors: (
          contactAddressErr.hasValidationErrors
          || inputErrors.hasValidationErrors || billingAddressErr.hasValidationErrors || billingInfoErr.hasValidationErrors
        ),
      };
      setAlertErrorMessages((prev) => ({ ...prev, ...billingError }));
      billingInformation = {
        ...billingInfo,
        address: {
          ...billingAddress,
          ...(billingAddress.country && {
            country: COUNTRY_LIST.find(({ countryId }) => countryId === billingAddress.country).name,
            state: stateList.find(({ id }) => id === billingAddress.state).name,
            phone: {
              ...billingAddress.phone,
              countryId: billingAddress.country,
            },
          }),
        },
      };
    } else {
      const billingError = {
        billingEmail: '',
        billingFirstName: '',
        billingLastName: '',
        billingCompany: '',
        billingPhone: '',
        billingCity: '',
        billingState: '',
        billingAddress1: '',
        billingZipCode: '',
      };
      setAlertErrorMessages((prev) => ({ ...prev, ...billingError }));
    }
    if (values({ ...inputErrors, ...contactAddressErr }).every(isEmpty) && values({ ...billingInfoErr, ...billingAddressErr }).every(isEmpty)) {
      const accountInput = {
        contactInformation,
        industryId: inputValues.industry,
        businessSegmentId: inputValues.businessSegment,
        billingInformation,
        shippingEmail,
        ...(inputValues?.refer && { customerReferralId: inputValues?.refer }),
        clientId,
        stateId: contactAddress.state,
        countryId: contactAddress.country,
      };
      try {
        const response = await createAccount(accountInput, is3Legged);
        const { ok, json } = response;
        if (ok) {
          const {
            email, userCreated, orgCreated, activationEmailSent,
          } = json;
          history.push('/registered', {
            email, userCreated, orgCreated, activationEmailSent,
          });
        } else {
          setServiceError({ createAccount: json.errorDescription });
          setLoading(false);
          scrollToTop();
        }
      } catch (err) {
        setServiceError({ createAccount: err.message });
        setLoading(false);
        scrollToTop();
      }
    } else setLoading(false);
  };

  const isNarrow = useMediaQuery(675);

  return (
    <EVBox wrap="nowrap" className={classes.topContainer}>
      <EVBackdrop
        className={classes.backdrop}
        open={loading}
      >
        <EVCircularProgress color="inherit" />
      </EVBackdrop>
      <EVGrid
        container
        wrap="nowrap"
        style={{
          flexDirection: 'row',
          height: '100%',
          position: 'relative',
        }}
      >
        {!isMobile && (
        <EVGrid
          data-testid="imageDiv"
          ref={imageDivRef}
          style={{
            display: 'flex',
            flex: '1',
            height: '100%',
            overflow: 'auto',
            backgroundImage: 'url(/assets/images/evbackground.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: '50%',
          }}
          onWheel={handleWheel}
        />
        )}
        <EVGrid data-testid="formDiv" ref={formDivRef} className={classes.formMainContainer}>
          <EVGrid className={classes.evLogoContainer}>
            <img
              src={evLogo}
              alt="EagleView"
              className={classes.logo}
            />
          </EVGrid>
          {
          (serviceError.businessSegment || serviceError.state || serviceError.createAccount)
          && (
            <EVBox id="serviceError" className={classes.errorContainer}>
              <EVTypography>{serviceError[Object.keys(serviceError)[0]]}</EVTypography>
            </EVBox>
          )
        }
          {alertErrorMessages.hasValidationErrors && (
            <ErrorsAlert
              inputErrors={alertErrorMessages}
              inputRefs={inputRefs}
              key={errorsKey}
            />
          )}
          <EVGrid
            className={classes.formContainer}
            style={{
              borderTop: (alertErrorMessages.hasValidationErrors || serviceError) ? 'none' : '1px solid #C8CDD7',
              padding: isNarrow ? '24px' : (alertErrorMessages.hasValidationErrors || serviceError) ? '0px 48px 0px 48px' : '24px 48px 0px 48px',
            }}
          >
            <EVTypography className={classes.titleHeader}>Create an Eagleview Account</EVTypography>
            {(serviceError.industry || serviceError.referer) ? (
              <EVGrid className={classes.container} style={{ alignItems: 'center' }}>
                <EVTypography style={{ color: 'black' }}>There was an error loading the data</EVTypography>
                <EVButton
                  style={{ width: 'auto' }}
                  onClick={retryApiCalls}
                  variant="contained"
                  color="primary"
                  className={classes.button}
                  startIcon={<EVRefreshOutlinedIcon />}
                >
                  Retry
                </EVButton>
              </EVGrid>
            ) : (
              <EVGrid>
                <EVGrid className={classes.container}>
                  <EVTypography className={classes.formHeader}>Login Information</EVTypography>
                  <InputField
                    labelText="Email address"
                    inputId="email"
                    inputValue={inputValues.email}
                    variant="outlined"
                    size="small"
                    handleChange={handleInputChange}
                    handleBlur={handleInputBlur}
                    errorMessage={errorMessages.email}
                    isRequired
                    ref={inputRefs.email}
                  />
                </EVGrid>
                <EVGrid className={classes.container}>
                  <EVTypography variant="h2" className={classes.formHeader}>Contact Information</EVTypography>
                  <InputField
                    labelText="First name"
                    inputId="firstName"
                    inputValue={inputValues.firstName}
                    variant="outlined"
                    size="small"
                    handleChange={handleInputChange}
                    handleBlur={handleInputBlur}
                    errorMessage={errorMessages.firstName}
                    isRequired
                    ref={inputRefs.firstName}
                  />
                  <InputField
                    labelText="Last name"
                    inputId="lastName"
                    inputValue={inputValues.lastName}
                    variant="outlined"
                    size="small"
                    handleChange={handleInputChange}
                    handleBlur={handleInputBlur}
                    errorMessage={errorMessages.lastName}
                    isRequired
                    ref={inputRefs.lastName}
                  />
                  <InputField
                    labelText="Company"
                    inputId="company"
                    inputValue={inputValues.company}
                    variant="outlined"
                    size="small"
                    handleChange={handleInputChange}
                    handleBlur={handleInputBlur}
                    errorMessage={errorMessages.company}
                    isRequired
                    ref={inputRefs.company}
                  />
                </EVGrid>
                <EVGrid className={classes.container}>
                  <EVTypography className={classes.formHeader}>Industry and Business Segment</EVTypography>
                  <EVGrid item style={{ width: '208px' }}>
                    <FormSelectField
                      labelText="Select industry"
                      id="industry"
                      selectId="industryId"
                      selectValue={inputValues.industry}
                      selectList={industryList.filter((industry) => industry.isExternal)}
                      handleSelectChange={handleInputChange}
                      handleBlur={handleInputBlur}
                      isRequired
                      errorMessage={errorMessages.industry}
                      defaultOption="Industry"
                      ref={inputRefs.industry}
                    />
                  </EVGrid>
                  <EVGrid item style={{ width: '208px', display: 'flex', alignItems: 'center' }}>
                    <FormSelectField
                      style={{ maxWidth: '208px' }}
                      labelText="Select business segment"
                      id="businessSegment"
                      selectId="id"
                      selectValue={inputValues.businessSegment}
                      selectList={businessSegmentList}
                      handleSelectChange={handleInputChange}
                      handleBlur={handleInputBlur}
                      errorMessage={errorMessages.businessSegment || serviceError.businessSegment}
                      defaultOption="Business Segment"
                      isRequired
                      isDisabled={!inputValues.industry}
                      ref={inputRefs.businessSegment}
                    />
                    {serviceError.businessSegment
                      && <EVButton style={{ textTransform: 'none' }} color="primary" onClick={handleBusinessSegmentUpdate}>Retry</EVButton>}
                  </EVGrid>
                </EVGrid>
                <EVGrid className={classes.container}>
                  <EVTypography className={classes.formHeader}>Address</EVTypography>
                  <FormSelectField
                    labelText="Country"
                    id="country"
                    selectId="countryId"
                    selectValue={contactAddress.country}
                    selectList={COUNTRY_LIST}
                    handleSelectChange={({ target: { value } }) => setContactAddress((prevAddr) => ({ ...prevAddr, country: value }))}
                    handleBlur={
                  () => setContactAddressErrors((prev) => ({ ...prev, country: validateAddressInput({ country: contactAddress.country }).country }))
                }
                    errorMessage={contactAddressErrors.country}
                    isRequired
                    defaultOption="Country"
                    ref={inputRefs.country}
                  />
                  <Address
                    inputValues={contactAddress}
                    errorMessages={contactAddressErrors}
                    setErrorMessages={setContactAddressErrors}
                    setInputValues={setContactAddress}
                    stateList={(contactAddress.country === 229 || contactAddress.country === 39) ? stateList : [...UsStateList, ...canadaStateList]}
                    stateLabelText={contactAddress.country === 39 ? 'Province' : contactAddress.country === 229 ? 'State' : 'State/ Province'}
                    inputId="contact"
                    intputRefs={{
                      phone: inputRefs.contactPhone,
                      city: inputRefs.contactCity,
                      state: inputRefs.contactState,
                      streetAddress1: inputRefs.contactAddress1,
                      zipCode: inputRefs.contactZipCode,
                    }}
                  />
                </EVGrid>
                <EVGrid className={classes.bottomContainer}>
                  <EVGrid className={classes.inputContainer}>
                    <EVTypography className={classes.formHeader}>Email Information</EVTypography>
                    <EVBox className={classes.checkbox}>
                      <EVCheckbox
                        style={{ width: '24px', height: '24px' }}
                        id="isEmailNotificationsAccepted"
                        color="primary"
                        checked={isEmailNotificationsAccepted}
                        onChange={handleEmailCheckboxChange}
                      />
                      <EVInputLabel className={classes.checkBoxLabel} htmlFor="isEmailNotificationsAccepted">
                        Send notifications and reports to my email
                      </EVInputLabel>
                    </EVBox>
                  </EVGrid>
                  {
                isEmailNotificationsAccepted
                || (
                  <InputField
                    labelText="Send my information to"
                    inputId="shippingEmail"
                    inputValue={inputValues.shippingEmail}
                    variant="outlined"
                    size="small"
                    handleChange={handleInputChange}
                    handleBlur={handleInputBlur}
                    errorMessage={errorMessages.shippingEmail}
                    isRequired
                    ref={inputRefs.shippingEmail}
                  />
                )
              }
                  <EVGrid className={classes.inputContainer}>
                    <EVTypography className={classes.formHeader}>Billing Information</EVTypography>
                    <EVGrid className={classes.checkbox}>
                      <EVCheckbox
                        style={{ width: '24px', height: '24px' }}
                        id="isBillingInfoSame"
                        color="primary"
                        checked={isBillingInfoSame}
                        onChange={handleBillingCheckboxChange}
                      />
                      <EVInputLabel className={classes.checkBoxLabel} htmlFor="isBillingInfoSame">
                        My billing information matches my contact information
                      </EVInputLabel>
                    </EVGrid>
                  </EVGrid>
                  {
                !isBillingInfoSame
                && (
                  <>
                    <InputField
                      labelText="First name"
                      inputId="billing-firstName"
                      inputValue={billingInfo.firstName}
                      variant="outlined"
                      size="small"
                      handleChange={handleBillingInfoChange}
                      handleBlur={handleBillingInputBlur}
                      errorMessage={billingInfoErrors.firstName}
                      isRequired
                      ref={inputRefs.billingFirstName}
                    />
                    <InputField
                      labelText="Last name"
                      inputId="billing-lastName"
                      inputValue={billingInfo.lastName}
                      variant="outlined"
                      size="small"
                      handleChange={handleBillingInfoChange}
                      handleBlur={handleBillingInputBlur}
                      errorMessage={billingInfoErrors.lastName}
                      isRequired
                      ref={inputRefs.billingLastName}
                    />
                    <InputField
                      labelText="Billing email"
                      inputId="billing-email"
                      inputValue={billingInfo.email}
                      variant="outlined"
                      size="small"
                      handleChange={handleBillingInfoChange}
                      handleBlur={handleBillingInputBlur}
                      errorMessage={billingInfoErrors.email}
                      isRequired
                      ref={inputRefs.billingEmail}
                    />
                    <InputField
                      labelText="Company"
                      inputId="billing-company"
                      inputValue={billingInfo.company}
                      variant="outlined"
                      size="small"
                      handleChange={handleBillingInfoChange}
                      handleBlur={handleBillingInputBlur}
                      errorMessage={billingInfoErrors.company}
                      isRequired
                      ref={inputRefs.billingCompany}
                    />
                    <FormSelectField
                      labelText="Country"
                      id="billing-country"
                      selectId="countryId"
                      selectValue={billingAddress.country}
                      selectList={COUNTRY_LIST}
                      handleSelectChange={handleBillingInfoChange}
                      handleBlur={handleBillingInputBlur}
                      errorMessage={billingAddressErrors.country}
                      defaultOption="Country"
                      isRequired
                      isDisabled
                    />
                    <Address
                      inputValues={billingAddress}
                      errorMessages={billingAddressErrors}
                      setErrorMessages={setBillingAddressErrors}
                      setInputValues={setBillingAddress}
                      stateList={(contactAddress.country === 229 || contactAddress.country === 39) ? stateList : [...UsStateList, ...canadaStateList]}
                      stateLabelText={contactAddress.country === 39 ? 'Province' : contactAddress.country === 229 ? 'State' : 'State/ Province'}
                      inputId="billing"
                      intputRefs={{
                        phone: inputRefs.billingPhone,
                        city: inputRefs.billingCity,
                        state: inputRefs.billingState,
                        streetAddress1: inputRefs.billingAddress1,
                        zipCode: inputRefs.billingZipCode,
                      }}
                    />
                  </>
                )
              }
                  <EVGrid container style={{ flexDirection: 'column' }}>
                    <EVGrid item style={{ width: '240px', display: 'flex', alignItems: 'center' }}>
                      <FormSelectField
                        id="refer"
                        labelText="How did you hear about us?"
                        errorMessage={errorMessages.refer}
                        selectId="id"
                        isRequired
                        selectValue={inputValues.refer}
                        selectList={refererList}
                        handleSelectChange={handleInputChange}
                        defaultOption="Referral Id"
                        ref={inputRefs.refer}
                      />
                    </EVGrid>
                  </EVGrid>
                  <EVGrid container direction="column">
                    <EVBox className={classes.checkbox}>
                      <EVCheckbox
                        style={{ width: '24px', height: '24px' }}
                        id="isTermsAccepted"
                        color="primary"
                        checked={isTermsAccepted}
                        onChange={handleTermsCheckboxChange}
                      />
                      <EVGrid container>
                        <EVInputLabel htmlFor="isTermsAccepted" className={classes.termsText}>
                          I have read and agree to the
                          {' '}
                          <EVLink className={classes.termsLink} href={TERMS_OF_USE_URL} target="_blank" underline="hover">Terms of Use</EVLink>
                          {' '}
                          and
                          {' '}
                          <EVLink className={classes.termsLink} href={PRIVACY_POLICY_URL} target="_blank" underline="hover">Privacy Policy</EVLink>
                          {' '}
                        </EVInputLabel>
                        <EVInputLabel htmlFor="isTermsAccepted" className={classes.customerText}>
                          Customers are advised not to disclose privacy information.
                        </EVInputLabel>
                      </EVGrid>
                    </EVBox>
                    <EVFormHelperText error>{errorMessages.terms}</EVFormHelperText>
                  </EVGrid>
                  <EVButton
                    id="submitBtn"
                    variant="contained"
                    color="primary"
                    className={classes.button}
                    onClick={handleSubmit}
                    disabled={!isTermsAccepted}
                  >
                    Create Account
                  </EVButton>
                </EVGrid>
              </EVGrid>
            )}
          </EVGrid>
        </EVGrid>
      </EVGrid>
    </EVBox>
  );
};

SignUp.propTypes = {
  flags: PropTypes.shape({
    enableGovtIndustry: PropTypes.bool,
  }).isRequired,
  ldClient: PropTypes.shape({
    identify: PropTypes.func.isRequired,
    allFlags: PropTypes.func.isRequired,
  }).isRequired,
};

export default withLDConsumer()(SignUp);
